import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  AcademicCapIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Classes = () => {
  const [selectedGrade, setSelectedGrade] = useState('kindergarten');
  const [activeDocument, setActiveDocument] = useState('welcome');
  const [availableDocuments, setAvailableDocuments] = useState({});
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  const grades = [
    { id: 'kindergarten', name: 'Kindergarten', shortName: 'K', color: 'bg-[#630503]' },
    { id: 'first', name: '1st Grade', shortName: '1st', color: 'bg-[#8B4513]' },
    { id: 'second', name: '2nd Grade', shortName: '2nd', color: 'bg-[#A0522D]' },
    { id: 'third', name: '3rd Grade', shortName: '3rd', color: 'bg-[#CD853F]' },
    { id: 'fourth', name: '4th Grade', shortName: '4th', color: 'bg-[#D2691E]' },
    { id: 'fifth', name: '5th Grade', shortName: '5th', color: 'bg-[#BC8F8F]' }
  ];

  // Function to check if an image exists
  const checkImageExists = (imagePath) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = imagePath;
    });
  };

  // Load available documents for each grade
  useEffect(() => {
    const loadDocuments = async () => {
      const docs = {};
      
      for (const grade of grades) {
        docs[grade.id] = {
          welcome: [],
          supply: []
        };
        
        // Define actual file patterns based on the existing file structure
        const filePatterns = [
          // Language-specific files (priority)
          { type: 'welcome', name: 'welcome-english.png', label: 'Welcome Letter (English)' },
          { type: 'welcome', name: 'welcome-spanish.png', label: 'Welcome Letter (Spanish)' },
          { type: 'supply', name: 'supply-english.png', label: 'Supply List (English)' },
          { type: 'supply', name: 'supply-spanish.png', label: 'Supply List (Spanish)' },
          
          // Base files (fallback)
          { type: 'welcome', name: 'welcome.png', label: 'Welcome Letter' },
          { type: 'supply', name: 'supply.png', label: 'Supply List' },
          
          // Numbered variations
          { type: 'supply', name: 'supply2.png', label: 'Supply List (Additional)' },
          { type: 'welcome', name: 'welcome2.png', label: 'Welcome Letter (Additional)' }
        ];
        
        // Check each file and only add if it exists
        for (const file of filePatterns) {
          const path = `/assets/documents/classes/${grade.id}/${file.name}`;
          
          try {
            const exists = await checkImageExists(path);
            if (exists) {
              docs[grade.id][file.type].push({
                path,
                label: file.label,
                name: file.name
              });
            }
          } catch (error) {
            console.log(`File not found: ${path}`);
          }
        }

        // Sort documents to ensure consistent order (language-specific first, then base, then numbered)
        ['welcome', 'supply'].forEach(type => {
          docs[grade.id][type].sort((a, b) => {
            // Priority order: English -> Spanish -> Base -> Numbered
            const getFilePriority = (name) => {
              if (name.includes('-english')) return 1;
              if (name.includes('-spanish')) return 2;
              if (/\d+/.test(name)) return 4; // numbered files last
              return 3; // base files
            };
            
            const aPriority = getFilePriority(a.name);
            const bPriority = getFilePriority(b.name);
            
            if (aPriority !== bPriority) {
              return aPriority - bPriority;
            }
            
            // If same priority, sort alphabetically
            return a.name.localeCompare(b.name);
          });
        });
      }
      
      setAvailableDocuments(docs);
      setDocumentsLoading(false);
    };
    
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGradeInfo = (gradeId) => {
    const gradeData = {
      kindergarten: {
        description: "Our Kindergarten program focuses on foundational skills, social-emotional development, and fostering a love for learning through play-based activities.",
        highlights: ["Social-Emotional Learning", "Phonics & Early Reading", "Number Sense", "Creative Expression"]
      },
      first: {
        description: "First grade builds upon kindergarten skills with more structured learning, independent reading, and mathematical problem-solving.",
        highlights: ["Independent Reading", "Addition & Subtraction", "Writing Skills", "Science Exploration"]
      },
      second: {
        description: "Second grade emphasizes reading fluency, mathematical reasoning, and developing critical thinking skills across all subjects.",
        highlights: ["Reading Fluency", "Place Value", "Research Skills", "Community Connections"]
      },
      third: {
        description: "Third grade marks a transition to more complex academics with multiplication, chapter books, and project-based learning.",
        highlights: ["Multiplication & Division", "Chapter Books", "Essay Writing", "Scientific Method"]
      },
      fourth: {
        description: "Fourth grade prepares students for upper elementary with advanced problem-solving, research projects, and leadership opportunities.",
        highlights: ["Complex Problem Solving", "Research Projects", "Leadership Skills", "Historical Thinking"]
      },
      fifth: {
        description: "Fifth grade serves as a bridge to middle school with advanced academics, independent projects, and preparation for future success.",
        highlights: ["Advanced Mathematics", "Independent Projects", "Public Speaking", "College & Career Ready"]
      }
    };
    return gradeData[gradeId] || gradeData.kindergarten;
  };

  return (
    <main id="main-content" className="bg-white">
      <Navbar />

      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Academic Programs
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                P.S. 11X <span className="font-bold">Grade Levels</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Explore our grade-level programs from Kindergarten through 5th Grade. Access welcome letters and supply lists in both English and Spanish.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Grade Selection */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Select a Grade Level</h2>
            <p className="text-gray-600">Choose a grade to view welcome letters and supply lists</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {grades.map((grade) => (
              <button
                key={grade.id}
                onClick={() => setSelectedGrade(grade.id)}
                className={`relative overflow-hidden px-6 py-4 rounded-xl font-bold text-sm uppercase tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                  selectedGrade === grade.id
                    ? `${grade.color} text-white shadow-2xl scale-105`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="relative flex flex-col items-center">
                  <AcademicCapIcon className="h-6 w-6 mb-2" />
                  <span className="text-xs">{grade.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grade Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Grade Description */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 h-full">
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 ${grades.find(g => g.id === selectedGrade)?.color} rounded-full flex items-center justify-center mr-4`}>
                    <span className="text-white text-xl font-bold">
                      {grades.find(g => g.id === selectedGrade)?.shortName}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {grades.find(g => g.id === selectedGrade)?.name}
                    </h3>
                    <p className="text-gray-600 text-sm">Academic Program</p>
                  </div>
                </div>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  {getGradeInfo(selectedGrade).description}
                </p>
                
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider">
                    Key Focus Areas
                  </h4>
                  <div className="space-y-2">
                    {getGradeInfo(selectedGrade).highlights.map((highlight, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-[#630503] mr-3 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Document Controls */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                {/* Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Grade Documents</h3>
                  <p className="text-gray-600">Available documents for {grades.find(g => g.id === selectedGrade)?.name}</p>
                </div>

                {/* Document Type Selection */}
                <div className="flex justify-center gap-4 mb-8">
                  <button
                    onClick={() => setActiveDocument('welcome')}
                    className={`flex items-center px-6 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all duration-300 shadow-md ${
                      activeDocument === 'welcome'
                        ? 'bg-[#630503] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Welcome Letters
                  </button>
                  <button
                    onClick={() => setActiveDocument('supply')}
                    className={`flex items-center px-6 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all duration-300 shadow-md ${
                      activeDocument === 'supply'
                        ? 'bg-[#630503] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Supply Lists
                  </button>
                </div>

                {/* Document Display */}
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 bg-gray-50 min-h-96">
                  {documentsLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#630503] mx-auto mb-4"></div>
                      <p className="text-gray-500 text-lg font-medium mb-2">Loading Documents...</p>
                      <p className="text-gray-600 text-sm">Checking for available documents...</p>
                    </div>
                  ) : availableDocuments[selectedGrade] && availableDocuments[selectedGrade][activeDocument] ? (
                    <div>
                      {availableDocuments[selectedGrade][activeDocument].length === 0 ? (
                        <div className="text-center py-12">
                          <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 text-lg font-medium mb-2">No Documents Available</p>
                          <p className="text-gray-600 text-sm">
                            {activeDocument === 'welcome' ? 'Welcome letters' : 'Supply lists'} for {grades.find(g => g.id === selectedGrade)?.name.toLowerCase()} are not yet available.
                          </p>
                        </div>
                      ) : (
                        <div className={`grid gap-6 ${availableDocuments[selectedGrade][activeDocument].length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                          {availableDocuments[selectedGrade][activeDocument].map((doc, index) => (
                            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                              <div className="p-4 bg-gray-50 border-b border-gray-200">
                                <h4 className="text-sm font-semibold text-gray-900 text-center">{doc.label}</h4>
                              </div>
                                                             <div className="p-4">
                                 <div 
                                   className="cursor-pointer transition-transform hover:scale-105"
                                   onClick={() => setEnlargedImage(doc)}
                                 >
                                   <img 
                                     src={doc.path}
                                     alt={doc.label}
                                     className="w-full h-auto object-contain max-h-96 rounded-lg"
                                     loading="lazy"
                                     onError={(e) => {
                                       // Hide the entire document container if image fails to load
                                       const container = e.target.closest('.bg-white.rounded-lg');
                                       if (container) {
                                         container.style.display = 'none';
                                       }
                                     }}
                                   />
                                 </div>
                                 <p className="text-center text-xs text-gray-500 mt-2">Click to enlarge</p>
                               </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg font-medium mb-2">No Documents Found</p>
                      <p className="text-gray-600 text-sm">Unable to load documents for this grade level.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Contact Information */}
      <section className="py-20 bg-[#630503]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-8">
              Questions About <span className="font-bold">Your Grade?</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-12">
              Our grade-level teams are here to support your child's academic journey. Contact us for more information about specific programs and requirements.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-[#630503] hover:bg-white/90 font-semibold px-8 py-4 text-base transition-colors rounded-lg">
                Contact Grade Team
              </button>
              <button className="bg-white/10 border border-white/30 text-white hover:bg-white/20 font-semibold px-8 py-4 text-base transition-colors rounded-lg">
                Schedule School Visit
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Image Enlargement Modal */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <div 
            className="relative max-w-5xl max-h-full bg-white rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#630503] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">{enlargedImage.label}</h3>
              <button
                onClick={() => setEnlargedImage(null)}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 max-h-[80vh] overflow-auto">
              <img 
                src={enlargedImage.path}
                alt={enlargedImage.label}
                className="w-full h-auto object-contain"
                style={{ maxHeight: '70vh' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.insertAdjacentHTML('afterend', '<div class="w-full h-64 bg-gray-200 flex items-center justify-center rounded-lg"><span class="text-gray-500 text-lg">Document not available</span></div>');
                }}
              />
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {grades.find(g => g.id === selectedGrade)?.name} - {enlargedImage.label}
                </p>
                <button
                  onClick={() => setEnlargedImage(null)}
                  className="bg-[#630503] text-white px-4 py-2 rounded-lg hover:bg-[#630503]/90 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Classes; 